import type {JOB_INFORMATION} from 'src/types/crm';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useDispatch, useSelector} from 'react-redux';
import * as reduxApi from 'src/utils/redux-api-v2';
import {
  ButtonTextExtraSmall,
  FormLabelSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';

import {TokenListInput} from '@spaced-out/ui-design-system/lib/components/TokenListInput';
import {useArbitraryOptionAddition} from '@spaced-out/ui-design-system/lib/hooks/useArbitraryOptionAddition';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';

import css from './basic-information.css';

import {integrationTypes} from 'src/components/crm/constant';
import {CRM_PERMISSIONS} from 'src/utils/crm';
import {selectHasCrmPermission, selectUserInfo} from 'src/selectors/crm';


type TokenOption = {
  key: string;
  label: string;
  arbitrary?: boolean;
};

type SkillsInformationProps = {
  handleJobUpdate: (key: string, value: string | string[]) => void;
  jobInformation: JOB_INFORMATION;
  skillType?: 'mandatorySkills' | 'keySkills';
};

export const SkillsInformation = ({
  handleJobUpdate,
  jobInformation,
  skillType,
}: SkillsInformationProps) => {
  const dispatch = useDispatch();
  const {t, makeKey} = useLabelTranslation();

  const [suggestedSkillsList, setSuggestedSkillsList] = React.useState<
    string[]
  >([]);
  const [suggestedKeySkillsList, setSuggestedKeySkillsList] = React.useState<
    string[]
  >([]);
  const [suggestedKeySkillsLoading, setSuggestedKeySkillsLoading] =
    React.useState(false);
  const [suggestedMandatorySkillsLoading, setSuggestedMandatorySkillsLoading] =
    React.useState(false);
  const [mandatorySkillsInput, setMandatorySkillsInput] = React.useState('');
  const [keySkillsInput, setKeySkillsInput] = React.useState('');

  const {organization: organizationInfo} = useSelector(selectUserInfo) ?? {};
  const {integrationType = 'NONE'} = organizationInfo ?? {};

  const isFieldLocked = integrationType === integrationTypes.SUCCESSFACTORS;

  const {mandatorySkills = [], keySkills = []} = jobInformation;
  const fetchSuggestedSkillsWithLoading = async (
    skills: string[],
    setLoading: (value: boolean) => void,
  ) => {
    const skillsParam = skills.join(',');
    setLoading(true);
    const response = await dispatch(
      reduxApi.get(
        `/api/autocomplete/similar-skills?skills=${skillsParam}`,
        {},
        {apiPath: '/trmapi'},
      ),
    );
    setLoading(false);
    return response;
  };

  React.useEffect(() => {
    if (mandatorySkills.length > 0) {
      fetchSuggestedSkillsWithLoading(
        mandatorySkills,
        setSuggestedMandatorySkillsLoading,
      ).then((response) => {
        if (response && Array.isArray(response)) {
          setSuggestedSkillsList(response);
        }
      });
    } else {
      setSuggestedSkillsList([]);
    }
  }, [mandatorySkills]);

  // Add new useEffect for key skills
  React.useEffect(() => {
    if (keySkills.length > 0) {
      fetchSuggestedSkillsWithLoading(
        keySkills,
        setSuggestedKeySkillsLoading,
      ).then((response) => {
        if (response && Array.isArray(response)) {
          setSuggestedKeySkillsList(response);
        }
      });
    } else {
      setSuggestedKeySkillsList([]);
    }
  }, [keySkills]);

  const handleUpdateSkills = (selectedSkills: TokenOption[], type: string) => {
    if (type === 'must') {
      const skillValues = selectedSkills.map((skill) => skill.key);
      // Replace the entire skills array with the new selection
      handleJobUpdate('mandatorySkills', skillValues);
    } else {
      const skillValues = selectedSkills.map((skill) => skill.key);
      // Replace the entire skills array with the new selection
      handleJobUpdate('keySkills', skillValues);
    }
  };

  const mapForSkillsInput = (array: string[]): TokenOption[] =>
    array.map((value) => ({key: value, label: value}));

  const mandatorySkillsOptions: TokenOption[] = React.useMemo(
    () => mapForSkillsInput(mandatorySkills),
    [mandatorySkills],
  );

  const keySkillsOptions: TokenOption[] = React.useMemo(
    () => mapForSkillsInput(keySkills),
    [keySkills],
  );

  const {optionsWithArbitrary: arbitraryMandatorySkillsOptions} =
    useArbitraryOptionAddition({
      searchTerm: mandatorySkillsInput,
      options: [],
      excludedKeys: mandatorySkills,
      makeArbitraryValue: (value) => ({
        key: value,
        label: value,
        arbitrary: true,
      }),
    });

  const {optionsWithArbitrary: arbitraryKeySkillsOptions} =
    useArbitraryOptionAddition({
      searchTerm: keySkillsInput,
      options: [],
      excludedKeys: keySkills,
      makeArbitraryValue: (value) => ({
        key: value,
        label: value,
        arbitrary: true,
      }),
    });

  return (
    <div className={css.informationContainer}>
      <div className={css.container}>
        {(!skillType || skillType === 'mandatorySkills') && (
          <div className={css.content}>
            <div className={css.informationContainer}>
              {!skillType && (
                <FormLabelSmall color="secondary">
                  {t('Must_Have_Skills', 'Must Have Skills')}
                </FormLabelSmall>
              )}
              <TokenListInput
                values={mandatorySkillsOptions}
                placeholder={t('Add_skills', 'Add skills')}
                onChange={(values: TokenOption[]) => {
                  handleUpdateSkills(values, 'must');
                }}
                limit={100}
                menu={{
                  options: arbitraryMandatorySkillsOptions.map((option) => ({
                    ...option,
                    label: t(makeKey(option.label), option.label),
                  })),
                }}
                onInputChange={(inputValue) => {
                  setMandatorySkillsInput(inputValue);
                }}
                inputValue={mandatorySkillsInput}
                disabled={isFieldLocked}
              />
              {!suggestedMandatorySkillsLoading && (
                <div className={css.row}>
                  {suggestedSkillsList.length > 0 && (
                    <ButtonTextExtraSmall color="secondary">
                      {t('Suggestions', 'Suggestions')}:{' '}
                    </ButtonTextExtraSmall>
                  )}
                  {suggestedSkillsList.map((mandatorySkill) => (
                    <Chip
                      size="small"
                      key={mandatorySkill}
                      onClick={() =>
                        handleJobUpdate(
                          'mandatorySkills',
                          Array.from(
                            new Set([...mandatorySkills, mandatorySkill]),
                          ),
                        )
                      }
                      disabled={isFieldLocked}
                    >
                      {t(makeKey(mandatorySkill), mandatorySkill)}
                    </Chip>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {(!skillType || skillType === 'keySkills') && (
          <div className={css.content}>
            <div className={css.informationContainer}>
              {!skillType && (
                <FormLabelSmall color="secondary">
                  {t('Good_to_Have_Skills', 'Good to Have Skills')}
                </FormLabelSmall>
              )}
              <TokenListInput
                values={keySkillsOptions}
                placeholder={t('Add_skills', 'Add skills')}
                onChange={(values: TokenOption[]) => {
                  handleUpdateSkills(values, 'key');
                }}
                limit={100}
                menu={{
                  options: arbitraryKeySkillsOptions.map((option) => ({
                    ...option,
                    label: t(makeKey(option.label), option.label),
                  })),
                  resolveLabel: (option: TokenOption) => {
                    if (option.arbitrary && option.label) {
                      return `${option.label}`;
                    }
                    return option.label || '';
                  },
                }}
                onInputChange={(inputValue) => {
                  setKeySkillsInput(inputValue);
                }}
                inputValue={keySkillsInput}
                disabled={isFieldLocked}
              />
              {!suggestedKeySkillsLoading && (
                <div className={css.row}>
                  {suggestedKeySkillsList.length > 0 && (
                    <ButtonTextExtraSmall color="secondary">
                      {t('Suggestions', 'Suggestions')}:{' '}
                    </ButtonTextExtraSmall>
                  )}
                  {suggestedKeySkillsList.map((keySkill) => (
                    <Chip
                      key={keySkill}
                      onClick={() =>
                        handleJobUpdate(
                          'keySkills',
                          Array.from(new Set([...keySkills, keySkill])),
                        )
                      }
                      size="small"
                      disabled={isFieldLocked}
                    >
                      {t(makeKey(keySkill), keySkill)}
                    </Chip>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
